import React, { useMemo } from 'react';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';

import type { Reservation } from '@luxuryescapes/contract-svc-reservation';

import OfferBuilderImagesByRoom from './OfferBuilderImagesByRoom';
import OfferBuilderLogoVideoSection from './OfferBuilderLogoVideoSection';
import OfferBuilderMainImageLibrary from './OfferBuilderMainImageLibrary';

interface Props {
  images?: Array<App.OfferImage>;
  propertyId?: string;
  propertyLogoId?: string;
  propertyImages?: Array<Reservation.Image>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  vendorId?: string;
  videos?: OfferVideoGroups;
}

export interface MediaSourceImage {
  id?: string;
  id_cloudinary_external?: string;
  id_image?: number | string;
  title?: string | null;
}

export type PropertyRoomTypeWithImages = App.RoomType & {
  images?: Array<MediaSourceImage>;
};

export interface MediaImage {
  publicId: string;
  sourceLabel: string;
  title?: string;
}

interface OfferVideoGroups {
  luxuryescapes?: {
    vimeo_id?: string | null;
  };
  scoopontravel?: {
    vimeo_id?: string | null;
  };
}

function addSourceImages({
  images,
  mediaImages,
  seenPublicIds,
  sourceLabel,
}: {
  images?: Array<MediaSourceImage>;
  mediaImages: Array<MediaImage>;
  seenPublicIds: Set<string>;
  sourceLabel: string;
}) {
  images?.forEach((image) => {
    const publicId = image.id_cloudinary_external;

    if (!publicId || seenPublicIds.has(publicId)) {
      return;
    }

    seenPublicIds.add(publicId);
    mediaImages.push({
      publicId,
      sourceLabel,
      title: image.title || undefined,
    });
  });
}

function buildMediaImages({
  offerImages,
  propertyImages,
  propertyRoomTypes,
}: {
  offerImages: Array<App.OfferImage>;
  propertyImages?: Array<Reservation.Image>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}) {
  const mediaImages: Array<MediaImage> = [];
  const seenPublicIds = new Set<string>();

  addSourceImages({
    images: offerImages,
    mediaImages,
    seenPublicIds,
    sourceLabel: 'Property hero',
  });
  addSourceImages({
    images: propertyImages,
    mediaImages,
    seenPublicIds,
    sourceLabel: 'Property hero',
  });

  propertyRoomTypes?.forEach((room) => {
    addSourceImages({
      images: room.images,
      mediaImages,
      seenPublicIds,
      sourceLabel: room.name ? `${room.name}` : 'Room',
    });
  });

  return mediaImages;
}

function getPrimaryVideoId(videos?: OfferVideoGroups) {
  const luxuryEscapesVideoId = videos?.luxuryescapes?.vimeo_id;

  if (luxuryEscapesVideoId && luxuryEscapesVideoId !== 'undefined') {
    return luxuryEscapesVideoId;
  }

  const scooponTravelVideoId = videos?.scoopontravel?.vimeo_id;

  if (scooponTravelVideoId && scooponTravelVideoId !== 'undefined') {
    return scooponTravelVideoId;
  }

  return undefined;
}

export default function OfferBuilderImagesVideo({
  images = [],
  propertyId,
  propertyLogoId,
  propertyImages,
  propertyRoomTypes,
  vendorId,
  videos,
}: Props) {
  const mediaImages = useMemo(
    () =>
      buildMediaImages({
        offerImages: images,
        propertyImages,
        propertyRoomTypes,
      }),
    [images, propertyImages, propertyRoomTypes],
  );
  return (
    <Box>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h4">Images & Video</Typography>
        <Typography variant="body1" color="text.secondary">
          Manage offer media from one place. Upload property images, organise them by room type, choose the property
          logo, and attach video content.
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 4 }}>
        <Button variant="outlined" startIcon={<DriveFolderUploadOutlinedIcon />}>
          Upload folder
        </Button>
        <Button variant="contained" startIcon={<AddPhotoAlternateOutlinedIcon />}>
          Add image
        </Button>
      </Stack>

      <OfferBuilderMainImageLibrary mediaImages={mediaImages} />

      <OfferBuilderImagesByRoom propertyRoomTypes={propertyRoomTypes} />
      <OfferBuilderLogoVideoSection
        propertyId={propertyId}
        propertyLogoId={propertyLogoId}
        vendorId={vendorId}
        videoId={getPrimaryVideoId(videos)}
        videoPosterImage={mediaImages[0]}
      />
    </Box>
  );
}
